// `asset_types` PUT dispatch for createFieldAddedVariant.
//
// The adapter generates a UUID v4, inserts the optimistic asset type
// locally, then this connector POSTs the same id to
// /capture/field-additions. Two outcomes are handled inline so the upload
// queue always drains (uploadData acks the whole batch only after every
// entry settles, so any throw on an EXPECTED response wedges the queue —
// the #656 single-ack class; see connector-upload-isolation.ts):
//
//   already_exists (409) — another row is authoritative for the same
//     logical variant. Record an id rewrite (orphan → authoritative) so
//     dependent capture_items POSTs target the authoritative id, then
//     delete the local orphan. The rewrite is recorded in BOTH the
//     per-batch DispatchContext (in-batch consumers) AND the durable
//     module-level registry (connector-asset-type-rewrite-registry.ts) so
//     a count uploading in a LATER batch — a >100-op offline session, or
//     after an app reload — still finds it (H19 #703). If the 409 carries
//     NO parseable replacement id (no details.authoritative.id /
//     details.existingAssetTypeId) the id-less sub-case DRAINS inline
//     (delete the orphan + Sentry + ack — destructive op FIRST so a throwing
//     Sentry can't strand the orphan and re-wedge the queue) instead of
//     rethrowing — a rethrow would wedge the whole queue because
//     already_exists is not in
//     PERMANENT_UPLOAD_CODES (latent on today's backend, which always
//     populates the id; #744). The local optimistic row is dropped either
//     way, and the field-add definition holds no count of its own.
//
//   permanent validation/client 4xx (H20 #695) — the server re-validated
//     the field-add and rejected it (e.g. its sub-system was removed, the
//     name failed re-validation, the role may not add variants, or a
//     code-less 4xx). It can NEVER succeed on retry and holds no count of
//     its own, so it is drained inline (orphan deleted FIRST, then
//     surfaced to Sentry — so a throwing Sentry can't strand the orphan)
//     instead of rethrown — a rethrow would wedge the whole queue forever.
//
// A transient failure (5xx / network / token refresh / rate-limit) still
// rethrows so the SDK retries the whole batch — the field-add WOULD land
// on retry, so it must not be drained.

import type { CrudEntry } from '@powersync/web'
import type { PowerSyncWriteDb } from './powersync-db'
import * as Sentry from '@sentry/react'

import { ApiError, apiFetch } from '../api'

import { recordAssetTypeRewrite } from './connector-asset-type-rewrite-registry'
import type { DispatchContext } from './connector-dispatch-context'
import { _emitUploaded } from './upload-events'

export async function dispatchAssetTypeInsert(
  entry: CrudEntry,
  db: PowerSyncWriteDb,
  context: DispatchContext,
): Promise<void> {
  const data = entry.opData ?? {}
  try {
    await apiFetch(`/capture/field-additions`, {
      method: 'POST',
      json: {
        id: entry.id,
        subSystemId: data['sub_system_id'],
        name: data['name'],
        unitOfMeasure: data['unit_of_measure'],
        groupingConvention: data['grouping_convention'],
      },
    })
    _emitUploaded(entry.table, entry.id)
    return
  } catch (err) {
    if (err instanceof ApiError && err.code === 'already_exists') {
      const replacementId = extractConflictingAssetTypeId(err)
      if (replacementId === null) {
        // An id-less 409: the server says the variant already exists but the
        // body carries neither details.authoritative.id nor
        // details.existingAssetTypeId, so there is no authoritative id to
        // re-home dependent counts onto. Rethrowing would WEDGE the whole
        // device queue forever — already_exists is deliberately NOT in
        // PERMANENT_UPLOAD_CODES (it normally re-homes), so the central
        // backstop would rethrow it too (#744). This op created NOTHING
        // server-side (the authoritative row already existed) and the
        // field-add definition holds no count of its own, so drain it inline
        // exactly like the permanent-4xx path below: delete the local optimistic
        // orphan, then surface loudly to Sentry, and ack. Any counts the surveyor
        // recorded against this orphan re-home/quarantine on their own POST
        // (createItemCore returns not_found for an absent asset-type FK so the
        // connector quarantines rather than wedges; see create-item.ts). Latent
        // today — the field-additions endpoint always populates the id on a 409
        // — but defended so the registry's `ack` claim for already_exists is
        // honest.
        const code = err.code ?? null
        // Destructive op FIRST (mirrors dropOrphanFloor): delete the orphan
        // BEFORE telemetry so a throwing Sentry.captureException (SDK misconfig
        // or a test mock) can't strand the orphan row — a surviving orphan would
        // re-fire this same id-less 409 next cycle and permanently wedge the
        // queue, the exact failure this drain exists to prevent. A real
        // db.execute rejection still propagates (we do not swallow it).
        await db.execute(`DELETE FROM asset_types WHERE id = ?`, [entry.id])
        // Telemetry is best-effort: the orphan is already gone, so a throwing
        // Sentry must NOT escape and rethrow (that would re-wedge the batch the
        // drain just unblocked). Swallow only the telemetry — never the DELETE.
        try {
          Sentry.captureException(err, {
            level: 'warning',
            extra: {
              reason: 'powersync_asset_type_conflict_no_replacement_id',
              table: entry.table,
              op: entry.op,
              entryId: entry.id,
              status: err.status,
              code,
              drained: true,
            },
          })
          console.warn(
            '[powersync] field-added asset type 409 already_exists carried no ' +
              'replacement id — drained to keep the upload queue moving; the ' +
              'local optimistic row is dropped and any counts captured against ' +
              'it are preserved in the device quarantine',
            { id: entry.id, status: err.status, code },
          )
        } catch {
          // best-effort telemetry — the orphan is already deleted
        }
        return
      }
      if (replacementId !== entry.id) {
        // In-batch consumers read the per-batch context map…
        context.assetTypeIdRewrites.set(entry.id, replacementId)
        // …and a dependent count uploading in a LATER batch reads the durable
        // module-level registry, so a >100-op / post-reload session that splits
        // the asset-type create from its counts still re-homes them (H19 #703).
        recordAssetTypeRewrite(entry.id, replacementId)
      }
      await db.execute(
        `DELETE FROM asset_types WHERE id = ?`,
        [entry.id],
      )
      return
    }
    // H20 #695: a permanent validation/client 4xx can never succeed on retry
    // and holds no count of its own. Rethrowing it forever wedges the whole
    // device upload queue behind this one entry. Drain it inline: delete the
    // orphan that can never sync FIRST, then surface loudly to Sentry (the
    // field-add definition is non-count data, so this mirrors the central
    // backstop's non-count path in connector-upload-isolation.ts), and return so
    // batch.complete() runs. Counts the surveyor recorded against
    // this just-rejected field-add reference an asset_type_id the server never
    // created, so each one's OWN capture_items POST gets not_found (createItemCore
    // returns not_found — never invalid_argument — for an absent asset-type FK
    // precisely so the connector QUARANTINES the count rather than wedging; see
    // create-item.ts) and is preserved in the device-local quarantine, surfaced
    // via the "held safely" toast. No count is silently dropped. Handled here —
    // not via the table-aware permanent set — so the central classifier stays
    // owned by its in-flight PR.
    if (err instanceof ApiError && isPermanentFieldAdditionError(err)) {
      // Destructive op FIRST (mirrors dropOrphanFloor): delete the orphan that
      // can never sync BEFORE telemetry, so a throwing Sentry.captureException
      // can't strand it and re-wedge the queue on the next cycle. A real
      // db.execute rejection still propagates unchanged.
      await db.execute(`DELETE FROM asset_types WHERE id = ?`, [entry.id])
      // Telemetry is best-effort: the orphan is already gone, so a throwing
      // Sentry must NOT escape and rethrow. Swallow only the telemetry.
      try {
        Sentry.captureException(err, {
          level: 'warning',
          extra: {
            reason: 'powersync_asset_type_field_addition_rejected',
            table: entry.table,
            op: entry.op,
            entryId: entry.id,
            status: err.status,
            code: err.code ?? null,
            drained: true,
          },
        })
        console.warn(
          '[powersync] field-added asset type rejected by the server ' +
            '(permanent 4xx) — drained to keep the upload queue moving; any ' +
            'counts captured against it are preserved in the device quarantine',
          { id: entry.id, status: err.status, code: err.code ?? null },
        )
      } catch {
        // best-effort telemetry — the orphan is already deleted
      }
      return
    }
    // Transient (5xx / network / token refresh / rate-limit): rethrow so the
    // SDK retries the whole batch. The field-add WOULD land on retry, so it
    // must NOT be drained — the temporary wedge self-clears once the condition
    // resolves (the #656 forever-wedge is specifically about PERMANENT errors).
    throw err
  }
}

// Transient 4xx statuses that MUST rethrow (retry), never drain: a field-add
// rejected by one of these WOULD succeed on a later attempt, so draining it
// would lose a recoverable variant. 401 → a token refresh fixes it; 408 → a
// request timeout; 409 → the already_exists path handled above; 429 → a
// rate-limit that self-clears.
const TRANSIENT_FIELD_ADDITION_STATUSES: ReadonlySet<number> = new Set([
  401, 408, 409, 429,
])

/**
 * True only for an `ApiError` whose response can NEVER succeed on retry with the
 * same payload — a permanent client error (validation / authorization / a moot
 * FK). STATUS-based, not code-based: the backend can return a code-less 4xx
 * (apiFetch leaves err.code undefined when the body has no `code`), and a
 * code-based test would let that wedge through (audit H20 #695). 5xx, a network
 * failure (status 0), and the transient-4xx set above all return false → the
 * caller rethrows → the SDK retries.
 */
function isPermanentFieldAdditionError(err: ApiError): boolean {
  if (err.status < 400 || err.status >= 500) return false
  return !TRANSIENT_FIELD_ADDITION_STATUSES.has(err.status)
}

function extractConflictingAssetTypeId(err: ApiError): string | null {
  const body = err.body
  const details =
    typeof body === 'object' && body !== null
      ? (body as Record<string, unknown>)['details']
      : null
  if (typeof details !== 'object' || details === null) {
    return null
  }

  const authoritative = (details as Record<string, unknown>)['authoritative']
  if (typeof authoritative === 'object' && authoritative !== null) {
    const authoritativeId = (authoritative as Record<string, unknown>)['id']
    if (typeof authoritativeId === 'string' && authoritativeId.length > 0) {
      return authoritativeId
    }
  }

  const existingAssetTypeId = (details as Record<string, unknown>)[
    'existingAssetTypeId'
  ]
  return typeof existingAssetTypeId === 'string' &&
    existingAssetTypeId.length > 0
    ? existingAssetTypeId
    : null
}
